export const DEFAULTS = {
  headingColor: "#202221",
  sectionTitleColor: "#2b6a64",
  paragraphColor: "#202221",
  labelColor: "#20413d",
  linkColor: "#2b6a64",
  ctaColor: "#d6f860",
  ctaTextColor: "#20413d",
  ctaAltColor: "#2b6a64",
  ctaAltTextColor: "#f9fdff",
  tabActiveColor: "#2b6a64",
  tabUnderlineColor: "#d6f860",
  topNavColor: "#20413d",
  topNavTextColor: "#f9fdff",
  sideNavColor: "#e9eeec",
  sideNavTextColor: "#20413d",
  activeNavColor: "#8e7ce0",
  accentColor: "#2b6a64",
  appBackgroundColor: "#eef1f3",
  cardBorderColor: "#e6ebea",
  inputBorderColor: "#d7dedd",
  rowDividerColor: "#f1f4f3",
  tableHeaderColor: "#f4f6f7",
  tableHeaderTextColor: "#5b6b68",
  mutedTextColor: "#94a3b8",
  outlineButtonColor: "#eef6f4",
  outlineBorderColor: "#cfe6e2",
};

const COLOR_VARS = {
  headingColor: "--color-heading",
  sectionTitleColor: "--color-section-title",
  paragraphColor: "--color-ink",
  labelColor: "--color-label",
  linkColor: "--color-link",
  ctaColor: "--color-cta",
  ctaTextColor: "--color-cta-ink",
  ctaAltColor: "--color-cta-alt",
  ctaAltTextColor: "--color-cta-alt-ink",
  tabActiveColor: "--color-tab-active",
  tabUnderlineColor: "--color-tab-underline",
  topNavColor: "--color-topnav",
  topNavTextColor: "--color-topnav-ink",
  sideNavColor: "--color-sidebar",
  sideNavTextColor: "--color-sidenav-ink",
  activeNavColor: "--color-pulse",
  accentColor: "--color-resolve",
  appBackgroundColor: "--color-app",
  cardBorderColor: "--color-card",
  inputBorderColor: "--color-field",
  rowDividerColor: "--color-divider",
  tableHeaderColor: "--color-table-head",
  tableHeaderTextColor: "--color-table-head-ink",
  mutedTextColor: "--color-ink-muted",
  outlineButtonColor: "--color-outline-fill",
  outlineBorderColor: "--color-outline",
};

export function withDefaults(theme, defaults) {
  const palette = defaults || DEFAULTS;
  const out = Object.assign({}, theme || {});
  Object.keys(palette).forEach((key) => {
    if (out[key] === null || out[key] === undefined || out[key] === "") out[key] = palette[key];
  });
  return out;
}

export const CLASSIC_DEFAULTS = {
  headingColor: "#101828",
  sectionTitleColor: "#000000",
  paragraphColor: "#364153",
  labelColor: "#101828",
  linkColor: "#ff0090",
  ctaColor: "#ff0090",
  ctaTextColor: "#ffffff",
  ctaAltColor: "#ff0090",
  ctaAltTextColor: "#ffffff",
  tabActiveColor: "#ff0090",
  tabUnderlineColor: "#ff0090",
  topNavColor: "#ffffff",
  topNavTextColor: "#6a7282",
  sideNavColor: "#1e2939",
  sideNavTextColor: "#99a1af",
  activeNavColor: "#101828",
  accentColor: "#ff0090",
  appBackgroundColor: "#f3f4f6",
  cardBorderColor: "#e5e7eb",
  inputBorderColor: "#e5e7eb",
  rowDividerColor: "#e5e7eb",
  tableHeaderColor: "#f3f4f6",
  tableHeaderTextColor: "#4a5565",
  mutedTextColor: "#6a7282",
  outlineButtonColor: "#ffcce9",
  outlineBorderColor: "#ffcce9",
};

function themeStyle(theme, baseline) {
  const style = {};
  if (!theme) return style;

  Object.keys(COLOR_VARS).forEach((key) => {
    const value = theme[key];
    if (value && String(value).toLowerCase() !== baseline[key]) {
      style[COLOR_VARS[key]] = value;
    }
  });

  return style;
}

export function typographyStyle(theme) {
  return themeStyle(theme, DEFAULTS);
}

export function classicStyle(theme) {
  return themeStyle(theme, CLASSIC_DEFAULTS);
}
export function hasCustomAccent(theme) {
  const value = theme && theme.accentColor;
  return !!value && String(value).toLowerCase() !== CLASSIC_DEFAULTS.accentColor;
}
export function toPayload(theme, defaults) {
  const t = withDefaults(theme, defaults);
  const payload = { themeId: t.themeId || null, name: t.name, isSelected: !!t.isSelected };
  Object.keys(DEFAULTS).forEach((key) => {
    payload[key] = t[key];
  });
  return payload;
}
export const THEME_NAME = "Customize Theme";

export function newTheme(name, defaults) {
  return Object.assign({ themeId: null, name: name || THEME_NAME, isSelected: true }, defaults || DEFAULTS);
}
export function currentTheme(themes, defaults) {
  return withDefaults(selectedOf(themes) || (themes && themes[0]) || newTheme(null, defaults), defaults);
}

export function selectedOf(themes) {
  if (!themes || !themes.length) return null;
  return themes.find((t) => t.isSelected) || null;
}

export async function loadThemes(axios, businessId) {
  try {
    const res = await axios.get(`/businesses/${businessId}/themes`);
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export function createTheme(axios, businessId, theme, defaults) {
  return axios.post(`/businesses/${businessId}/themes`, toPayload(theme, defaults));
}

export function updateTheme(axios, businessId, theme, defaults) {
  return axios.put(`/businesses/${businessId}/themes/${theme.themeId}`, toPayload(theme, defaults));
}

export function saveTheme(axios, businessId, theme, defaults) {
  return theme.themeId ? updateTheme(axios, businessId, theme, defaults) : createTheme(axios, businessId, theme, defaults);
}
